import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { Chips, type ChipsChangeEvent } from "primereact/chips";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchReferences } from "../../../../../api/application/reference";
import { fetchTables } from "../../../../../api/application/table";
import type { APIMethodType, APITable } from "../../../../../api/infrastructure/table.formatter";
import { getGeneratedReferences } from "../../../../../api/shared/get-references";
import { apiRequestWrapper } from "../../../../../api/shared/http-client";
import { useAppStore } from "../../../../../store/app.store";
import type { ImportTableDataInputState } from "../../../../../store/store-types";
import { showToast } from "../../../../shared/global-tools/toast-system";
import { flattenObjectKeys, type ParsedData } from "../../../../shared/parse-imported-file";
import { parseHeaders, validateHeaders } from "../../../../shared/validate-rest-api-headers";
import Icon from "../../../utils/Icons/Icon";
import OurDropdown from "../../../utils/OurDropdown/OurDropdown";
import Spinner from "../../../utils/Spinner/Spinner";
import DropFileZone, { type ImportFileResult } from "./DropFileZone";
import ImportTypeCard from "./ImportTypeCard";
import "./index.scss";

const API_METHODS = [
  { label: "GET", value: "GET" },
  { label: "POST", value: "POST" },
];

const AUTH_TYPES = [
  { label: "Aucune", value: "none" },
  { label: "Bearer", value: "bearer" },
  { label: "Clé API", value: "apiKey" },
];

export interface ImportDataStepOneProps {
  navigateToStep(step: number): void;
}

export default function ImportDataStepOne({ navigateToStep }: ImportDataStepOneProps) {
  const wrapAsync = useWrapAsync();
  // TODO: Add form validation
  const [errors] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<number>();
  const [selectedTableId, setSelectedTableId] = useState<string>();
  const [importType, setImportType] = useState<"API" | "FILE">("API");
  const [url, setUrl] = useState("");
  const [method, setMethod] = useState<APIMethodType>("GET");
  const [authType, setAuthType] = useState<"none" | "bearer" | "apiKey">("none");
  const [authToken, setAuthToken] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [importedData, setImportedData] = useState<ImportFileResult>();
  const {
    generatedReferences,
    setGeneratedReferences,
    importTableDataInputState,
    setImportTableDataInputState,
  } = useAppStore();
  const [tables, setTables] = useState<APITable[]>();
  const [loadingTables, setLoadingTables] = useState(false);
  const [loadingReferences, setLoadingReferences] = useState(false);
  const headersFieldRef = useRef<HTMLLabelElement>(null);

  // Merge any header still typed in the Chips input (not yet committed with Enter)
  // with the already-validated chips, so it isn't lost when testing/importing.
  const collectHeaders = (): string[] => {
    const input = headersFieldRef.current?.querySelector("input");
    const pending = input?.value.trim();
    if (!pending) return headers;
    if (!validateHeaders([pending])) {
      showToast(`L'entête '${pending}' est invalide`, { severity: "warn" });
      return headers;
    }
    const merged = headers.includes(pending) ? headers : [...headers, pending];
    setHeaders(merged);
    if (input) input.value = "";
    return merged;
  };

  const goToNextStep = wrapAsync(async () => {
    try {
      setLoading(true);
      const validForm = await validateForm();
      if (!validForm.valid) return;
      setImportTableDataInputState({
        ...importTableDataInputState,
        fromFile: validForm.input.fromFile,
        fromAPI: validForm.input.fromAPI,
        referenceId: validForm.input.referenceId,
        tableId: validForm.input.tableId,
      });
      navigateToStep(2);
    } catch (error) {
      console.error(error);
      showToast("Une erreur est survenue lors de la navigation vers l'étape suivante.", {
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  });

  const validateForm = async (): Promise<
    { valid: false } | { valid: true; input: ImportTableDataInputState }
  > => {
    if (!selectedDatabaseId) {
      showToast("Veuillez sélectionner un référentiel.", { severity: "warn" });
      return { valid: false };
    }
    if (!selectedTableId) {
      showToast("Veuillez sélectionner une table.", { severity: "warn" });
      return { valid: false };
    }
    if (importType === "API") {
      if (!url) {
        showToast("Veuillez entrer l'URL de l'API.", { severity: "warn" });
        return { valid: false };
      }
      if (authType === "bearer" && !authToken) {
        showToast("Veuillez entrer le token d'authentification.", { severity: "warn" });
        return { valid: false };
      }
      const effectiveHeaders = collectHeaders();
      const connectionSuccess = await testConnection(url, {
        method,
        authType,
        authToken,
        headers: effectiveHeaders,
      });
      if (!connectionSuccess.success) {
        showToast("Echec du test de connexion.", { severity: "error" });
        return { valid: false };
      }
      return {
        valid: true,
        input: {
          fromAPI: {
            apiUrl: url,
            method,
            authType,
            apiToken: authType === "bearer" ? authToken : undefined,
            headers: effectiveHeaders,
            columnsMapping: importTableDataInputState?.fromAPI?.columnsMapping || {},
            parsedData: connectionSuccess.parsedData,
          },
          fromFile: undefined,
          referenceId: selectedDatabaseId,
          tableId: Number(selectedTableId),
        },
      };
    }

    if (!importedData || !importedData.parsedData || importedData.parsedData.data.length === 0) {
      showToast("Veuillez importer un fichier avec des données.", { severity: "warn" });
      return { valid: false };
    }

    return {
      valid: true,
      input: {
        fromAPI: undefined,
        fromFile: {
          file: importedData.file as File,
          columnsMapping: importTableDataInputState?.fromFile?.columnsMapping || {},
          parsedData: importedData.parsedData,
        },
        referenceId: selectedDatabaseId,
        tableId: Number(selectedTableId),
      },
    };
  };

  const handleConnectionTest = wrapAsync(async () => {
    if (!url || !authType || (authType === "bearer" && !authToken)) {
      showToast("Veuillez remplir tous les champs requis avant de tester la connexion.", {
        severity: "warn",
      });
      return;
    }
    const effectiveHeaders = collectHeaders();
    const result = await testConnection(url, {
      method,
      authType,
      authToken,
      headers: effectiveHeaders,
    });
    if (result.success) {
      showToast("Test de connexion réussie !", { severity: "success" });
    } else {
      showToast("Echec du test de connexion.", { severity: "error" });
    }
  });

  useEffect(
    wrapAsync(async () => {
      if (generatedReferences) return;
      setLoadingReferences(true);
      await apiRequestWrapper(async () => {
        const result = await fetchReferences({ limit: 200, page: 1 });
        setGeneratedReferences(getGeneratedReferences(result.data));
      });
      setLoadingReferences(false);
    }),
    [generatedReferences],
  );
  useEffect(
    wrapAsync(async () => {
      if (!selectedDatabaseId) return;
      setLoadingTables(true);
      await apiRequestWrapper(async () => {
        const result = await fetchTables(selectedDatabaseId);
        setTables(result);
      });
      setLoadingTables(false);
    }),
    [selectedDatabaseId],
  );
  useEffect(() => {
    if (!importTableDataInputState) return;
    setSelectedDatabaseId(importTableDataInputState.referenceId);
    setSelectedTableId(String(importTableDataInputState.tableId));
    if (importTableDataInputState.fromAPI) {
      setImportType("API");
      setUrl(importTableDataInputState.fromAPI.apiUrl);
      setMethod(importTableDataInputState.fromAPI.method);
      setAuthType(importTableDataInputState.fromAPI.authType);
      setAuthToken(importTableDataInputState.fromAPI.apiToken || "");
      setHeaders(importTableDataInputState.fromAPI.headers ?? []);
    } else if (importTableDataInputState.fromFile) {
      setImportType("FILE");
      setImportedData(importTableDataInputState.fromFile);
    }
  }, [importTableDataInputState]);

  const databaseOptions = useMemo(
    () =>
      generatedReferences?.map((r) => ({
        value: r.id,
        label: r.name,
      })) ?? [],
    [generatedReferences],
  );

  const tableOptions = useMemo(
    () =>
      tables?.map((table) => ({
        value: table.id,
        label: table.label,
      })) ?? [],
    [tables],
  );

  return (
    <div className="ImportDataStepOne">
      <div className="ImportDataStepOne-card ImportDataStepOne-content">
        <div>
          <h2 className="ImportDataStepOne-title">Configuration de la source de données</h2>
          <div className="ImportDataStepOne-databaseChoice">
            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Référentiels
              </span>
              {loadingReferences ? (
                <div className="w-full">
                  <Spinner />
                </div>
              ) : (
                <OurDropdown<number>
                  initialOptions={databaseOptions}
                  className="sm RelationCard-dropdown"
                  selectedOption={selectedDatabaseId}
                  onChange={(val) => {
                    setSelectedDatabaseId(val);
                    setSelectedTableId(undefined);
                  }}
                  placeholder="Sélectionner"
                />
              )}
            </label>

            {loadingTables ? (
              <div className="w-full">
                <Spinner />
              </div>
            ) : (
              <label className="Field w-full">
                <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                  Table
                </span>
                <OurDropdown<string>
                  initialOptions={tableOptions}
                  className="sm RelationCard-dropdown"
                  selectedOption={selectedTableId}
                  onChange={setSelectedTableId}
                  placeholder="Sélectionner"
                />
              </label>
            )}
          </div>
        </div>

        <div className="ImportDataStepOne-importTypeChoice">
          <h2 className="ImportDataStepOne-title">Source de données</h2>
          <div className="ImportDataStepOne-importTypeCards">
            <ImportTypeCard
              active={importType === "API"}
              onClick={() => setImportType("API")}
              label="API"
              description="Import depuis une API REST"
              iconName="relationship"
            />
            <ImportTypeCard
              active={importType === "FILE"}
              onClick={() => setImportType("FILE")}
              label="Fichier"
              description="CSV"
              iconName="download"
            />
          </div>
        </div>

        {importType === "API" ? (
          <div className="ImportDataStepOne-card">
            <h2 className="ImportDataStepOne-title">Configuration API</h2>
            <div className="ImportDataStepOne-formGroup">
              <label className="Field w-full">
                {/* TODO: standardize styles */}
                <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
                  URL de l'API
                </span>
                <InputText
                  className="Field-input small TableColumnAPIForm-input w-full"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                  }}
                  required
                />
              </label>

              <label className="Field w-full">
                {/* TODO: standardize styles */}
                <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
                  Méthode HTTP
                </span>

                <OurDropdown<APIMethodType>
                  initialOptions={API_METHODS}
                  className="sm TableColumnAPIForm-dropdown w-full"
                  selectedOption={method}
                  onChange={setMethod}
                />
              </label>
            </div>

            <div className="ImportDataStepOne-formGroup">
              <label className="Field w-full">
                {/* TODO: standardize styles */}
                <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
                  Type d'authentification
                </span>

                <OurDropdown<"none" | "bearer" | "apiKey">
                  initialOptions={AUTH_TYPES}
                  className="sm TableColumnAPIForm-dropdown w-full"
                  selectedOption={authType}
                  onChange={setAuthType}
                />
              </label>

              {(authType === "bearer" || authType === "apiKey") && (
                <label className="Field w-full">
                  {/* TODO: standardize styles */}
                  <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
                    Token
                  </span>
                  <InputText
                    className="Field-input small TableColumnAPIForm-input w-full"
                    value={authToken}
                    onChange={(e) => {
                      setAuthToken(e.target.value);
                    }}
                    required={authType === "bearer"}
                  />
                </label>
              )}
            </div>

            <label className="Field w-full" ref={headersFieldRef}>
              {/* TODO: standardize styles */}
              <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
                Headers (key=value)
              </span>
              <Chips
                className="TableColumnAPIForm-chips w-full"
                value={headers}
                onChange={(e: ChipsChangeEvent) => {
                  if (!validateHeaders(e.value)) {
                    showToast(`L'entête '${e.value}' est invalide`, {
                      severity: "warn",
                    });
                    return;
                  }
                  setHeaders(e.value ?? []);
                }}
                placeholder="Content-Type=application/json"
              />
            </label>

            <Button
              className="Btn success sm icon"
              label="Tester la connexion"
              icon={<Icon name="relationship" />}
              onClick={handleConnectionTest}
              disabled={errors.size > 0 || loading}
            />
          </div>
        ) : (
          <DropFileZone importedData={importedData} onImport={setImportedData} />
        )}
      </div>

      <Button
        style={{ display: "flex", justifySelf: "flex-end", marginTop: "37px" }}
        className="Btn primary"
        label="Continuer"
        onClick={goToNextStep}
        disabled={errors.size > 0 || loading}
      />
    </div>
  );
}

async function testConnection(
  url: string,
  options: {
    method: APIMethodType;
    authType: "none" | "bearer" | "apiKey";
    authToken: string;
    headers: string[];
  },
): Promise<{ success: boolean; parsedData?: ParsedData }> {
  try {
    const requestHeaders: Record<string, string> = { ...parseHeaders(options.headers) };
    if (options.authType === "bearer") {
      requestHeaders.Authorization = `Bearer ${options.authToken}`;
    }
    const res = await fetch(url, {
      method: options.method,
      headers: requestHeaders,
    });
    if (!res.ok) {
      return { success: false };
    }
    const data = await res.json();
    const formatedData: Record<string, unknown>[] = [];
    if (!Array.isArray(data)) {
      if (typeof data === "object" && data !== null) {
        formatedData.push(data);
      }
    } else {
      for (const item of data) {
        if (typeof item === "object" && item !== null && !Array.isArray(item)) {
          formatedData.push(item);
        }
      }
    }
    return {
      success: true,
      parsedData: {
        data: formatedData,
        objectKeys: flattenObjectKeys(formatedData[0] ?? {}),
      },
    };
  } catch (error) {
    return { success: false };
  }
}
